/**
 * Shared course domain types, fetch helper, and formatters.
 *
 * Module: lib/courses
 * Layer:  util / data-access
 *
 * Purpose:
 *     De-duplicates the Course/Module/Lesson/Enrollment shapes and the
 *     `fetchJson` + duration/size formatters that the courses list,
 *     detail, and learn pages all need. Keeping them here satisfies DRY
 *     and gives every page a single typed boundary for the courses API.
 *
 * AI context:
 *     - Entry points called by: src/app/courses/** pages and their
 *       colocated card components.
 *     - Depends on: the browser `fetch` API only.
 *     - Side effects: network I/O via {@link fetchJson}.
 *     - Invariants: callers handle the rejection from fetchJson; this
 *       module never swallows errors.
 *     - Replacement cost: LOW.
 *
 * Threat model:
 *     - Responses are app-owned JSON; callers must not render raw HTML
 *       from these fields (React escaping handles text output).
 */

/** A single lesson within a course module. */
export interface Lesson {
  readonly id: number;
  readonly title: string;
  readonly slug: string;
  readonly description: string;
  readonly video_duration_seconds: number;
  readonly is_free_preview: boolean;
  readonly sort_order: number;
  readonly content_type: string;
  readonly video_url?: string | null;
  readonly content?: string | null;
  readonly resources?: readonly LessonResource[];
}

/** A downloadable asset attached to a lesson. */
export interface LessonResource {
  readonly id: number;
  readonly title: string;
  readonly file_url: string;
  readonly file_type: string;
  readonly file_size: number;
}

/** A group of lessons. */
export interface Module {
  readonly id: number;
  readonly title: string;
  readonly description: string;
  readonly sort_order: number;
  readonly lessons: readonly Lesson[];
}

/** A learner-authored review of a course. */
export interface Review {
  readonly id: number;
  readonly rating: number;
  readonly title: string;
  readonly content: string;
  readonly reviewer_name: string;
  readonly created_at: string;
}

/** Summary shape returned by the course-listing endpoints. */
export interface CourseSummary {
  readonly id: number;
  readonly title: string;
  readonly slug: string;
  readonly short_description: string;
  readonly thumbnail_url: string | null;
  readonly instructor_name: string;
  readonly price: number;
  readonly compare_at_price: number | null;
  readonly is_free: boolean;
  readonly is_featured: boolean;
  readonly total_duration_minutes: number;
  readonly lesson_count: number;
  readonly average_rating: number;
  readonly review_count: number;
  readonly enrollment_count: number;
  readonly membership_tier_name: string | null;
}

/** Full course detail including curriculum and reviews. */
export interface CourseDetail {
  readonly id: number;
  readonly title: string;
  readonly slug: string;
  readonly description: string;
  readonly short_description: string;
  readonly thumbnail_url: string | null;
  readonly promo_video_url: string | null;
  readonly instructor_name: string;
  readonly instructor_bio: string | null;
  readonly price: number;
  readonly compare_at_price: number | null;
  readonly is_free: boolean;
  readonly membership_tier_name: string | null;
  readonly total_duration_minutes: number;
  readonly lesson_count: number;
  readonly average_rating: number;
  readonly review_count: number;
  readonly enrollment_count: number;
  readonly modules: readonly Module[];
  readonly reviews: readonly Review[];
  readonly what_you_learn: readonly string[];
  readonly requirements: readonly string[];
}

/** Minimal course shape used by the lesson player. */
export interface CourseForLearning {
  readonly id: number;
  readonly title: string;
  readonly slug: string;
  readonly modules: readonly Module[];
}

/** A learner's enrollment + progress for a course. */
export interface Enrollment {
  readonly id: number;
  readonly progress_percent: number;
  readonly completed_lessons: readonly number[];
  readonly last_lesson_id: number | null;
}

/** A course category facet with its course count. */
export interface Category {
  readonly name: string;
  readonly slug: string;
  readonly count: number;
}

/**
 * Fetch and parse JSON from the credentialed courses API.
 *
 * @param path - API path (same-origin).
 * @param init - Optional request overrides.
 * @returns The parsed JSON body typed as `T`.
 * @throws Error when the response status is not ok.
 * @remarks Caller owns error handling; this never returns on failure.
 */
export async function fetchJson<T>(path: string, init?: RequestInit): Promise<T> {
  const res = await fetch(path, { credentials: 'include', ...init });
  if (!res.ok) {
    throw new Error(`${res.status} ${res.statusText}`);
  }
  return res.json() as Promise<T>;
}

/**
 * Format a minute count as a compact `Xh Ym` / `Xm` label.
 *
 * @param minutes - Non-negative whole minutes.
 * @returns A human-readable duration string.
 */
export function formatMinutes(minutes: number): string {
  if (minutes < 60) {
    return `${minutes}m`;
  }
  const hours = Math.floor(minutes / 60);
  const mins = minutes % 60;
  return mins > 0 ? `${hours}h ${mins}m` : `${hours}h`;
}

/**
 * Format seconds as a `M:SS` clock label for lesson rows.
 *
 * @param seconds - Non-negative whole seconds.
 * @returns A `minutes:seconds` string (seconds zero-padded).
 */
export function formatClock(seconds: number): string {
  const minutes = Math.floor(seconds / 60);
  const secs = seconds % 60;
  return `${minutes}:${secs.toString().padStart(2, '0')}`;
}

/**
 * Format a byte count as a `B` / `KB` / `MB` label.
 *
 * @param bytes - Non-negative byte count.
 * @returns A human-readable size string.
 */
export function formatFileSize(bytes: number): string {
  if (bytes < 1024) {
    return `${bytes} B`;
  }
  if (bytes < 1024 * 1024) {
    return `${(bytes / 1024).toFixed(1)} KB`;
  }
  return `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
}

/**
 * Format a verbose total-length label for course curriculum headers.
 *
 * @param minutes - Non-negative whole minutes.
 * @returns A spelled-out duration (e.g. `2 hours 5 minutes`).
 */
export function formatTotalDuration(minutes: number): string {
  if (minutes < 60) {
    return `${minutes} minutes`;
  }
  const hours = Math.floor(minutes / 60);
  const mins = minutes % 60;
  return mins > 0 ? `${hours} hours ${mins} minutes` : `${hours} hours`;
}
